import { defineStore } from "pinia";
import {
  decodeVisibilityPreview,
  encodeVisibilityPreview,
  overrideForToggle,
  PREVIEW_HIDE,
  PREVIEW_SHOW,
} from "%/dynamic-component-editor/helpers/visibility-preview";
import { collectFrameCallouts } from "%/dynamic-component-editor/helpers/visibility-callouts";

const CALLOUTS_ENABLED_KEY = "dce:visibility-callouts-enabled";

function readCalloutsEnabled() {
  try {
    return window.localStorage.getItem(CALLOUTS_ENABLED_KEY) !== "false";
  } catch {
    return true;
  }
}

// Task 206. Preview overrides are client-only: never an option write, never a /transactions POST.
// The URL + followers see them through injected seams (setVisibilityPreviewProvider / presence store).
export const useCanvasVisibilityPreviewStore = defineStore("canvas-visibility-preview-store", {
  state: () => ({
    dynamicComponentId: null,
    overrides: {},
    calloutsEnabled: readCalloutsEnabled(),
    // { [elementId]: { [instanceKey]: { order, baseVisible, ancestorIds, input } } }, selected breakpoint only.
    registry: {},
    // Focused frame's callout gutters (canvas px); the canvas fit makes room for them.
    calloutGutters: { left: 0, right: 0 },
  }),
  getters: {
    overrideFor: state => id => state.overrides[String(id).split(":")[0]] ?? null,
    encoded: state => encodeVisibilityPreview(state.dynamicComponentId, state.overrides),
    frameCallouts: state => collectFrameCallouts(state.registry, state.overrides),
  },
  actions: {
    setCalloutGutters({ left = 0, right = 0 } = {}) {
      if (left !== this.calloutGutters.left || right !== this.calloutGutters.right) {
        this.calloutGutters = { left, right };
      }
    },
    setVisibilityPreviewProvider({ read, write } = {}) {
      this._provider = {
        read: typeof read === "function" ? read : () => "",
        write: typeof write === "function" ? write : null,
      };
      if (this.dynamicComponentId !== null) {
        this.hydrateFromProvider();
      }
    },
    // Called when the editor opens a DC; a different DC drops the old page's overrides.
    setDynamicComponent(dynamicComponentId) {
      const next = dynamicComponentId === null || dynamicComponentId === undefined ? null : Number(dynamicComponentId);
      if (next === this.dynamicComponentId) {
        return;
      }
      this.dynamicComponentId = next;
      this.overrides = {};
      this.registry = {};
      this.hydrateFromProvider();
    },
    hydrateFromProvider() {
      const raw = this._provider?.read?.() ?? "";
      if (!raw || this.dynamicComponentId === null) {
        return;
      }
      const decoded = decodeVisibilityPreview(raw);
      if (decoded.dc !== null && decoded.dc !== this.dynamicComponentId) {
        // Left over from the previous page: not an error, just stale.
        this.persist();
        return;
      }
      if (decoded.problems.length) {
        // eslint-disable-next-line no-console
        console.warn(`Visibility preview from the URL ("${raw}") was not fully applied: ${decoded.problems.join("; ")}`);
      }
      this.overrides = decoded.overrides;
      this.persist();
    },
    toggle(id, show, baseVisible) {
      this.setOverride(id, overrideForToggle(baseVisible, show));
    },
    setOverride(id, verb) {
      const key = String(id).split(":")[0];
      const next = { ...this.overrides };
      if (verb === PREVIEW_SHOW || verb === PREVIEW_HIDE) {
        next[key] = verb;
      } else {
        delete next[key];
      }
      this.overrides = next;
      this.persist();
    },
    // Follow mode: the leader's view replaces ours wholesale (only for the same DC; caller checks).
    replaceAll(overrides) {
      this.overrides = { ...overrides };
      this.persist();
    },
    clear() {
      this.overrides = {};
      this.persist();
    },
    persist() {
      this._provider?.write?.(this.encoded);
    },
    setCalloutsEnabled(enabled) {
      this.calloutsEnabled = Boolean(enabled);
      try {
        window.localStorage.setItem(CALLOUTS_ENABLED_KEY, String(this.calloutsEnabled));
      } catch {
        // Private mode / blocked storage: the switch still works for this session.
      }
    },
    register(elementId, instanceKey, entry) {
      const id = String(elementId);
      this.registry = { ...this.registry, [id]: { ...this.registry[id], [instanceKey]: entry } };
    },
    unregister(elementId, instanceKey) {
      const id = String(elementId);
      if (!this.registry[id]?.[instanceKey]) {
        return;
      }
      const rest = { ...this.registry[id] };
      delete rest[instanceKey];
      const next = { ...this.registry };
      if (Object.keys(rest).length) {
        next[id] = rest;
      } else {
        delete next[id];
      }
      this.registry = next;
    },
  },
});

export default useCanvasVisibilityPreviewStore;
